import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../global.css';
import Navbar from '../../components/Navbar';
import SiteFooter from '../../components/SiteFooter';
import { useSEO } from '../../hooks/useSEO';

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 6 L5 9 L10 3" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function Home() {
  useSEO({
    title: 'AI that understands Africa',
    description: 'Cybernate is Africa\'s AI platform — trained on African languages, African data, and African realities. Transforming healthcare, agriculture, finance, safety, and education across the continent.',
    path: '/',
  });
  return (
    <>
      <div className="atm-grid" aria-hidden="true" />
      <div className="atm-noise" aria-hidden="true" />
      <div className="wrap">
        <Navbar />
        <Hero />
        <LangStrip />
        <UseCases />
        <Vision />
        <DeveloperCallout />
        <Capabilities />
        <Metrics />
        <Pricing />
        <CTASection />
        <SiteFooter />
      </div>
    </>
  );
}

function HeroLottie() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;
    let anim;
    import('lottie-web/build/player/lottie_canvas').then(mod => {
      if (cancelled || !ref.current) return;
      anim = mod.default.loadAnimation({
        container: ref.current,
        renderer: 'canvas',
        loop: true,
        autoplay: true,
        path: '/assets/anim/ai animation Flow 1.json',
        rendererSettings: { clearCanvas: true, progressiveLoad: true },
      });
      anim.setSpeed(0.7);
    });
    return () => { cancelled = true; anim?.destroy(); };
  }, []);
  return <div ref={ref} className="hero-lottie" aria-hidden="true" />;
}

function Hero() {
  return (
    <section className="hero">
      <div className="container">
        <div className="hero-grid">
          <div className="hero-inner">
            <div className="status-pill">
              <span className="status-dot" />
              Now live · 4 African regions
            </div>
            <h1 className="hero-title">
              General intelligence,<br />
              <em>built for </em><span className="accent">Africa.</span>
            </h1>
            <p className="hero-sub">
              Most AI was built for a world that doesn't look like ours. Cybernate is the platform
              trained on Africa's languages, shaped by Africa's realities — so whether you're
              a doctor in Accra, a farmer in Kano, a student in Nairobi, or a builder with an idea,
              you get intelligence that actually works for you.
            </p>
            <div className="hero-cta">
              <Link to="/signup" className="btn btn-primary btn-lg">
                Get early access
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              </Link>
              <Link to="/chat" className="btn btn-ghost btn-lg">Talk to our AI</Link>
            </div>
          </div>
          <HeroLottie />
        </div>
      </div>
      <div className="hero-meta">
        <div className="col"><span>People</span><strong>1.4B across Africa</strong></div>
        <div className="col"><span>Languages</span><strong>18 African languages</strong></div>
        <div className="col"><span>Sectors</span><strong>Health · Agri · Finance · Safety</strong></div>
        <div className="col"><span>Regions</span><strong>Lagos · Nairobi · Johannesburg</strong></div>
        <div className="col"><span>Mission</span><strong>AI built for Africa, by Africa</strong></div>
      </div>
    </section>
  );
}

function LangStrip() {
  const langs = [
    'ENGLISH', 'SWAHILI', 'HAUSA', 'YORUBA', 'AMHARIC', 'ARABIC',
    'ZULU', 'PORTUGUESE', 'SOMALI', 'OROMO', 'IGBO', 'FRENCH',
    'TIGRINYA', 'WOLOF', 'AFRIKAANS', 'SHONA', 'FULA', 'AKAN',
  ];
  const all = [...langs, ...langs];
  return (
    <div className="lang-strip">
      <div className="lang-strip-inner">
        {all.map((l, i) => <span key={i}>&middot; {l}</span>)}
      </div>
    </div>
  );
}

function UseCases() {
  return (
    <section className="section" id="usecases">
      <div className="container">
        <div className="line-mark">02 / SOLUTIONS</div>
        <div className="section-head">
          <div>
            <h2 className="section-title">
              The brains powering<br />
              <em>the continent's hardest problems.</em>
            </h2>
          </div>
          <p className="section-desc">
            We build the intelligence layer. Our partners build the products that reach millions
            of Africans every single day.
          </p>
        </div>
        <div className="usecase-grid">
          {[
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3L20 6V12C20 16.5 16.5 20 12 21C7.5 20 4 16.5 4 12V6L12 3Z" stroke="var(--fg)" strokeWidth="1.2"/>
                  <circle cx="12" cy="12" r="2.2" fill="var(--accent)"/>
                </svg>
              ),
              tag: 'Sector 01 · Safety',
              title: 'Keeping communities safe, in real time.',
              desc: 'When a threat emerges in Lagos, Nairobi, or Accra, our AI processes thousands of signals — from community reports, cameras, and social media — and routes help to the right place, in the right language, instantly.',
              highlights: [['LIVE IN', '4 cities today'], ['DAILY REACH', '2.4M signals processed'], ['PARTNER', 'Sety']],
              to: '/solutions/safety',
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="var(--fg)" strokeWidth="1.2"/>
                </svg>
              ),
              tag: 'Sector 02 · Health',
              title: 'Helping doctors get the right diagnosis.',
              desc: 'AI tools misdiagnosed African women of breast cancer — trained on the wrong populations, the wrong data. Cybernate is built on African patient realities: our diseases, our bodies, our clinical settings. Doctors get it right.',
              highlights: [['ACCURACY', '34% better than Western-only AI'], ['LANGUAGES', '18 for patient intake'], ['WORKS ON', 'Low-bandwidth, rural clinics']],
              to: '/solutions/health',
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22V12" stroke="var(--fg)" strokeWidth="1.2" strokeLinecap="round"/>
                  <path d="M12 12C12 12 6 10 5 4C8 4 12 7 12 12Z" stroke="var(--accent)" strokeWidth="1.2" strokeLinejoin="round"/>
                  <path d="M12 15C12 15 17 12 20 7C17 6 12 10 12 15Z" stroke="var(--fg)" strokeWidth="1.2" strokeLinejoin="round"/>
                  <path d="M5 22H19" stroke="var(--fg)" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              ),
              tag: 'Sector 03 · Agriculture',
              title: 'A farming expert in every pocket.',
              desc: 'A smallholder farmer in Kano snaps a photo of a sick crop on a $10 phone. In seconds, Cybernate identifies the disease and replies in Hausa with exactly what to do. No agronomist needed. No internet required.',
              highlights: [['ACCURACY', '94% crop disease detection'], ['LANGUAGES', '18 local languages'], ['WORKFORCE', '60% of Africa farms']],
              to: '/solutions/agriculture',
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 3L5 14H11L10 21L19 9H13L13 3Z" stroke="var(--fg)" strokeWidth="1.2" strokeLinejoin="round" fill="rgba(139,124,255,0.1)"/>
                </svg>
              ),
              tag: 'Sector 04 · Energy',
              title: "Power that's always there.",
              desc: "Unreliable electricity costs African businesses billions every year. Our AI monitors grids, predicts failures before they cause outages, and optimizes solar systems — keeping the lights on, from Lagos to Lusaka.",
              highlights: [['FORECASTING', '72 hours ahead'], ['RELIABILITY', '94% fault detection'], ['COVERAGE', '12 African climate zones']],
              to: '/solutions/energy',
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="var(--fg)" strokeWidth="1.2"/>
                  <path d="M15 9H10.5C9.67 9 9 9.67 9 10.5S9.67 12 10.5 12H13.5C14.33 12 15 12.67 15 13.5S14.33 15 13.5 15H9" stroke="var(--accent)" strokeWidth="1.2" strokeLinecap="round"/>
                  <path d="M12 7.5V9M12 15V16.5" stroke="var(--fg)" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              ),
              tag: 'Sector 05 · Finance',
              title: 'Bringing financial access to everyone.',
              desc: "Most Africans don't have a formal credit history. Cybernate reads mobile money patterns to assess creditworthiness — so anyone with a phone can access loans, insurance, and savings, in any language, over any channel.",
              highlights: [['OPPORTUNITY', '$180B African AI market'], ['SPEED', 'Fraud flagged in under 1 second'], ['CHANNELS', 'M-Pesa, USSD, WhatsApp']],
              to: '/solutions/fintech',
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="7" cy="8" r="2.4" stroke="var(--fg)" strokeWidth="1.2"/>
                  <circle cx="17" cy="8" r="2.4" stroke="var(--fg)" strokeWidth="1.2"/>
                  <circle cx="12" cy="17" r="2.4" fill="var(--accent)"/>
                  <line x1="9" y1="10" x2="11" y2="15" stroke="var(--fg)" strokeWidth="1.2"/>
                  <line x1="15" y1="10" x2="13" y2="15" stroke="var(--fg)" strokeWidth="1.2"/>
                </svg>
              ),
              tag: 'Sector 06 · Social & Community',
              title: 'Communities that truly understand each other.',
              desc: "Africa has over 2,000 languages. Communication is power. Cybernate powers voice agents, cooperative tools, and community platforms in every major African language — so no one is left out of the conversation.",
              highlights: [['LANGUAGES', '18 live · 6 in training'], ['VOICE', 'Natural speech in every language'], ['USE CASES', 'Credit, content, cooperatives']],
              to: null,
            },
          ].map(({ icon, tag, title, desc, highlights, to }) => (
            <div className="usecase" key={tag}>
              <div className="usecase-icon">{icon}</div>
              <div className="usecase-tag">{tag}</div>
              <h3 className="usecase-title">{title}</h3>
              <p className="usecase-desc">{desc}</p>
              <div className="usecase-foot">
                {highlights.map(([k, v]) => (
                  <div className="row" key={k}><span>{k}</span><span className="v">{v}</span></div>
                ))}
                {to && (
                  <Link to={to} style={{ color: 'var(--accent)', fontSize: 12, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Learn more →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Vision() {
  const futures = [
    { number: '01', sector: 'Healthcare', headline: 'No more wrong diagnoses.', tagline: 'Every clinic gets AI that understands African bodies, diseases, and languages.' },
    { number: '02', sector: 'Agriculture', headline: 'Africa feeds the world.', tagline: 'AI precision farming turns smallholder farmers into the world\'s breadbasket.' },
    { number: '03', sector: 'Manufacturing', headline: 'Made in Africa.', tagline: 'Intelligent quality control and supply chains fuel Africa\'s industrial rise.' },
    { number: '04', sector: 'Robotics', headline: 'The automation wave.', tagline: 'AI and robots working together in mining, logistics, and manufacturing.' },
    { number: '05', sector: 'Finance', headline: 'A billion people enter the economy.', tagline: 'Mobile money patterns unlock credit for those who were always creditworthy.' },
    { number: '06', sector: 'Education', headline: 'Every child, a world-class teacher.', tagline: 'A personal tutor in your mother tongue, on any phone, in any village.' },
  ];

  return (
    <section className="section" id="vision">
      <div className="container">
        <div className="line-mark">03 / THE FUTURE WE ARE BUILDING</div>
        <div className="section-head" style={{ marginBottom: 56 }}>
          <div>
            <h2 className="section-title">
              We are building the intelligence<br />
              <em>for Africa's next century.</em>
            </h2>
          </div>
          <p className="section-desc">
            Automation, manufacturing, healthcare, agriculture — the continent is at an inflection point. We chose acceleration.
          </p>
        </div>

        <div style={{ borderTop: '1px solid var(--line)' }}>
          {futures.map(({ number, sector, headline, tagline }) => (
            <div key={number} className="vision-row">
              <div className="vision-num" style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-faint)', letterSpacing: '0.06em' }}>{number}</div>
              <div className="vision-sector" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>{sector}</div>
              <div className="vision-hl" style={{ fontSize: 16, fontWeight: 500, color: 'var(--fg)', letterSpacing: '-0.02em' }}>{headline}</div>
              <div className="vision-tag" style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.55 }}>{tagline}</div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 48,
          padding: '28px 32px',
          background: 'var(--bg-1)',
          border: '1px solid var(--line-2)',
          borderRadius: 'var(--r-lg)',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: 32,
        }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Our north star</div>
            <p style={{ fontSize: 16, color: 'var(--fg)', lineHeight: 1.55, maxWidth: 680 }}>
              "These are the products our partners are building today — and Cybernate is the intelligence that powers all of it."
            </p>
          </div>
          <Link to="/signup" className="btn btn-primary" style={{ flexShrink: 0 }}>
            Be part of it
          </Link>
        </div>
      </div>
    </section>
  );
}

function DeveloperCallout() {
  return (
    <section className="section" id="developers">
      <div className="container">
        <div className="line-mark">04 / FOR DEVELOPERS</div>
        <div className="code-section">
          <div>
            <h2 className="section-title">Built for builders.<br /><em>Documented for humans.</em></h2>
            <p className="section-desc" style={{ marginTop: 20, marginBottom: 32 }}>
              REST API, Node.js and Python SDKs, streaming, vision, voice, and embeddings —
              all under one key. Full technical depth for the teams who need it.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <Link to="/developers" className="btn btn-primary">Developer platform</Link>
              <Link to="/docs" className="btn btn-ghost">Read the docs</Link>
            </div>
          </div>
          <div style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
          }}>
            <div style={{
              padding: '12px 18px',
              borderBottom: '1px solid var(--line)',
              background: 'var(--bg-2)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--fg-dim)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>Developer platform</span>
              <span style={{ color: 'var(--accent)' }}>cybernate.ai/developers</span>
            </div>
            <div style={{ padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                ['Node.js SDK', 'npm install cybernate-ai', 'var(--accent)'],
                ['Python SDK', 'pip install cybernate', 'var(--signal)'],
                ['REST API', 'curl api.cybernate.ai/v1', 'var(--fg-mid)'],
              ].map(([label, cmd, color]) => (
                <div key={label} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: 16,
                  borderBottom: '1px solid var(--line)',
                }}>
                  <span style={{ fontSize: 14, color: 'var(--fg)', fontWeight: 500 }}>{label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color }}>{cmd}</span>
                </div>
              ))}
              <Link to="/developers" style={{ fontSize: 13, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                Full documentation and code examples →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Capabilities() {
  const caps = [
    ['01', 'Speaks your language', 'Not translated — natively trained. Hausa, Yoruba, Swahili, Amharic, Zulu, and 13 more. These languages are not an afterthought. They are the foundation.'],
    ['02', 'Trained on African data', 'Our models were built on African contexts, not retrofitted from Western AI. African patients. African fields. African cities. African people.'],
    ['03', 'Works where you are', 'Optimized for Africa\'s real networks. Responsive on 2G. Offline-capable for areas where connectivity is inconsistent. No connection left behind.'],
    ['04', 'Your data stays in Africa', 'Data centres in Lagos, Nairobi, and Johannesburg. Your patients\' records, your customers\' data, your business intelligence — it never leaves the continent.'],
    ['05', 'Sees and hears everything', 'Reads X-rays, crop photos, ID documents, and handwriting. Understands voice notes, live calls, and real-time video in African languages.'],
    ['06', 'Compliant with African law', 'NDPR (Nigeria), POPIA (South Africa), Kenya Data Protection Act. Regulatory compliance built in from day one — not retrofitted.'],
    ['07', 'Understands African culture', 'Knows the texture of African markets, customs, and communication patterns. Not a one-size-fits-all model with an African flag slapped on it.'],
    ['08', 'Scales with your ambition', 'From a startup in Accra serving 100 customers, to a bank serving millions across East Africa. The platform grows with you.'],
  ];
  return (
    <section className="section" id="platform">
      <div className="container">
        <div className="line-mark">05 / WHY CYBERNATE</div>
        <div className="section-head">
          <div><h2 className="section-title">Built differently,<br /><em>for Africa.</em></h2></div>
          <p className="section-desc">Not a Western AI reskinned for the continent. Something built from the ground up, for the people who will actually use it.</p>
        </div>
        <div className="cap-grid">
          {caps.map(([n, t, d]) => (
            <div className="cap" key={n}>
              <div className="cap-num">{n}</div>
              <div className="cap-title">{t}</div>
              <div className="cap-desc">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Metrics() {
  return (
    <section className="section">
      <div className="container">
        <div className="line-mark">06 / OUR IMPACT</div>
        <div className="metrics">
          <div className="metric">
            <div className="metric-value">2.4<span className="small">M</span></div>
            <div className="metric-label">People reached daily through our partners</div>
          </div>
          <div className="metric">
            <div className="metric-value">&lt;1<span className="small">s</span></div>
            <div className="metric-label">Response time, every time, anywhere in Africa</div>
          </div>
          <div className="metric">
            <div className="metric-value">18</div>
            <div className="metric-label">African languages — spoken, not just translated</div>
          </div>
          <div className="metric">
            <div className="metric-value">4</div>
            <div className="metric-label">Regions, from Lagos to Johannesburg</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const plans = [
    {
      name: 'Explore', price: '$0', nameColor: null,
      desc: 'For individuals, students, and anyone curious about what AI can do for them. Free forever.',
      feats: [
        'Free to start, always',
        'Chat, vision, voice and more',
        'All 18 African languages',
        'No credit card, no demo call',
        'Community support',
      ],
      cta: { label: 'Start for free', to: '/signup', cls: 'btn btn-ghost' },
    },
    {
      name: 'Business', price: 'Pay as you go', nameColor: 'var(--accent)',
      featured: true,
      desc: 'For organizations using AI to drive real results. Simple, usage-based pricing — pay only for what you use.',
      feats: [
        'Every capability included',
        'Scales with your traffic',
        'Faster responses in your country',
        'Voice, vision, and chat',
        'Priority email & Slack support',
      ],
      cta: { label: 'Get early access', to: '/signup', cls: 'btn btn-primary' },
    },
    {
      name: 'Enterprise', price: 'Custom', nameColor: null,
      desc: 'Dedicated infrastructure, your data, your rules. For governments and large organizations.',
      feats: [
        'Dedicated infrastructure',
        'AI trained on your own data',
        'Full regulatory compliance',
        'Guaranteed uptime, 24/7',
        'Dedicated expert on your team',
      ],
      cta: { label: 'Talk to us', href: 'mailto:sales@cybernate.ai', cls: 'btn btn-ghost' },
    },
  ];
  return (
    <section className="section" id="pricing">
      <div className="container">
        <div className="line-mark">07 / PRICING</div>
        <div className="section-head">
          <div><h2 className="section-title">Simple plans,<br /><em>for every stage.</em></h2></div>
          <p className="section-desc">Start free. Grow without surprise bills. Large organizations get dedicated infrastructure and support.</p>
        </div>
        <div className="plans">
          {plans.map(p => (
            <div key={p.name} className={`plan${p.featured ? ' featured' : ''}`}>
              <div className="plan-name" style={p.nameColor ? { color: p.nameColor } : {}}>{p.name}</div>
              <div className="plan-price">{p.price}</div>
              <div className="plan-desc">{p.desc}</div>
              <ul className="plan-feats">
                {p.feats.map(f => <li key={f}><CheckIcon />{f}</li>)}
              </ul>
              {p.cta.to
                ? <Link to={p.cta.to} className={p.cta.cls}>{p.cta.label}</Link>
                : <a href={p.cta.href} className={p.cta.cls}>{p.cta.label}</a>
              }
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="section cta-section">
      <div className="container">
        <div className="cta-frame">
          <div className="cta-rule" />
          <div className="cta-grid">
            <div>
              <div className="kicker"><span className="kicker-num">08</span> Join the movement</div>
              <h2 className="cta-headline">Africa's AI future<br /><em>starts&nbsp;with&nbsp;you.</em></h2>
              <p className="cta-lede">
                Sign up today — no technical knowledge, no credit card, no demo call required.
                Whether you're a student in Lagos, a nurse in Kigali, a farmer in Kano, a bank
                in Nairobi, or a builder with an idea — Cybernate was built for you.
                Every African. The continent is waiting.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/signup" className="btn btn-primary btn-lg">
                  Get early access
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                </Link>
                <Link to="/chat" className="btn btn-ghost btn-lg">Talk to our AI first</Link>
              </div>
            </div>
            <div className="cta-right">
              <div className="cta-spec">
                {[
                  ['FREE TO START', 'yes'],
                  ['CARD REQUIRED', 'no'],
                  ['DEMO CALL', 'no'],
                  ['LANGUAGES', '18 African languages'],
                  ['FOR', 'Everyone — individuals, businesses, builders'],
                  ['STATUS', 'Live today'],
                ].map(([k, v]) => (
                  <div className="spec-row" key={k}>
                    <span className="spec-k">{k}</span>
                    <span className="spec-v">{v}</span>
                  </div>
                ))}
              </div>
              <div className="cta-pulse">
                <div className="pulse-dot" />
                <span>platform is live · accepting new users</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
